import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { Layers3, AlertCircle } from 'lucide-react';

/**
 * Fund-of-Funds Look-through panel — Task #12.
 *
 * Hosts inside Alternatives → Portfolio Companies workbench (FoF pill). Reads
 * from `/api/alternatives/fof/{fundId}/lookthrough` and renders three things:
 *
 *  1. Fund picker — only funds that actually have underlying child funds
 *     (probed via /has-underlying). Direct-investment funds are filtered out
 *     so the picker doesn't lie to the user.
 *  2. Roll-up summary — total attributed NAV + provenance (oldest/newest mark
 *     dates, missing-valuation count).
 *  3. Leaf table — every (path, portco) row with cumulative path %, leaf
 *     ownership %, leaf equity, and attributed value. Multiple rows per portco
 *     when the FoF reaches it via more than one path.
 *
 * Dark institutional theme — matches the rest of Alternatives.
 */

interface FundOption {
  id: number;
  fundName: string;
  fundType?: string;
  vintageYear?: number;
}

interface LookthroughLeaf {
  pathFundId: number;
  portcoId: number;
  portcoName: string;
  sector?: string;
  pathDepth: number;
  pathOwnershipPct?: number;
  portcoOwnershipPct?: number;
  portcoEquityValue?: number;
  attributedValue?: number;
  markDate?: string;
}

interface LookthroughResult {
  rootFundId: number;
  asOfDate: string;
  totalNav: number;
  leaves: LookthroughLeaf[];
  missingValuations: number;
  oldestMarkDate?: string;
  newestMarkDate?: string;
}

interface DepthBucket {
  depth: number;
  totalNav: number;
  portcoCount: number;
  topLeaves: LookthroughLeaf[];
}

type ViewMode = 'leaves' | 'byDepth' | 'topExposures';

const Wrap = styled.div`
  padding: 1.25rem 1.25rem 2rem;
  color: #cbd5e1;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;

  svg {
    width: 18px;
    height: 18px;
    color: #93c5fd;
  }
`;

const Title = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  color: #e2e8f0;
`;

const Subtitle = styled.div`
  font-size: 0.78rem;
  color: #94a3b8;
  margin-top: 2px;
`;

const Picker = styled.select`
  font-family: inherit;
  font-size: 0.85rem;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cbd5e1;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  min-width: 280px;
  cursor: pointer;

  &:focus {
    outline: 2px solid rgba(59, 130, 246, 0.4);
    outline-offset: 1px;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  margin-bottom: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
`;

const SummaryCell = styled.div`
  padding: 1rem 1.25rem;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  &:last-child { border-right: 0; }
`;

const SummaryLabel = styled.div`
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;
  font-weight: 600;
`;

const SummaryValue = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.25rem;
  color: #e2e8f0;
  font-weight: 500;
`;

const SummarySub = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  margin-top: 4px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.65rem 0.85rem;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(15, 23, 42, 0.4);
`;

const Td = styled.td`
  padding: 0.7rem 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  color: #cbd5e1;
  vertical-align: top;
`;

const TdMono = styled(Td)`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
`;

const TdRight = styled(TdMono)`
  text-align: right;
`;

const DepthChip = styled.span<{ $direct: boolean }>`
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 0.65rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 600;
  background: ${(p) => (p.$direct ? 'rgba(34, 197, 94, 0.12)' : 'rgba(139, 92, 246, 0.14)')};
  color: ${(p) => (p.$direct ? '#86efac' : '#c4b5fd')};
  border: 1px solid ${(p) => (p.$direct ? 'rgba(34, 197, 94, 0.3)' : 'rgba(139, 92, 246, 0.3)')};
`;

const ViewToggle = styled.div`
  display: inline-flex;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 2px;
  margin-right: 0.75rem;
`;

const ViewBtn = styled.button<{ $active: boolean }>`
  padding: 0.4rem 0.85rem;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 600;
  border: 0;
  border-radius: 4px;
  background: ${(p) => (p.$active ? 'rgba(59, 130, 246, 0.25)' : 'transparent')};
  color: ${(p) => (p.$active ? '#bfdbfe' : '#94a3b8')};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    color: #e2e8f0;
  }
`;

const DepthSliderWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  margin-right: 0.75rem;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 0.4rem 0.75rem;

  label {
    font-size: 0.72rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #94a3b8;
    font-weight: 600;
  }

  input[type='range'] {
    width: 110px;
    accent-color: #3b82f6;
  }

  span.val {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.78rem;
    color: #bfdbfe;
    min-width: 28px;
    text-align: right;
  }
`;

const DepthBucketCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
  margin-bottom: 1rem;
  overflow: hidden;
`;

const DepthBucketHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.1rem;
  background: rgba(15, 23, 42, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  user-select: none;

  &:hover {
    background: rgba(15, 23, 42, 0.55);
  }
`;

const DepthBucketTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;

  .label {
    font-size: 0.85rem;
    color: #e2e8f0;
    font-weight: 600;
  }
  .count {
    font-size: 0.72rem;
    color: #94a3b8;
  }
`;

const DepthBucketRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  color: #e2e8f0;
  font-weight: 600;
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-block;
  transition: transform 0.15s;
  transform: ${(p) => (p.$open ? 'rotate(90deg)' : 'rotate(0)')};
  color: #64748b;
  font-size: 0.85rem;
`;

const EmptyState = styled.div`
  padding: 3rem 1.5rem;
  text-align: center;
  color: #94a3b8;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 8px;

  svg {
    width: 28px;
    height: 28px;
    color: #64748b;
    margin-bottom: 0.75rem;
  }
`;

const fmtMoney = (v?: number) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

const fmtPct = (v?: number, decimals = 2) => {
  if (v == null) return '—';
  // Service stores ownership_pct as decimal (0.65 = 65%); render as %.
  return `${(v * 100).toFixed(decimals)}%`;
};

const FoFLookthroughPanel: React.FC = () => {
  const [searchParams] = useSearchParams();
  const fundIdFromUrl = searchParams.get('fundId');
  const [funds, setFunds] = useState<FundOption[]>([]);
  const [fofFunds, setFofFunds] = useState<FundOption[]>([]);
  const [selectedFundId, setSelectedFundId] = useState<number | null>(
    fundIdFromUrl ? Number(fundIdFromUrl) : null,
  );
  const [result, setResult] = useState<LookthroughResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('leaves');
  const [maxDepth, setMaxDepth] = useState<number>(10);
  const [topLimit, setTopLimit] = useState<number>(10);
  const [byDepthData, setByDepthData] = useState<Record<number, DepthBucket> | null>(null);
  const [byDepthLoading, setByDepthLoading] = useState(false);
  const [byDepthError, setByDepthError] = useState<string | null>(null);
  const [topExposures, setTopExposures] = useState<LookthroughLeaf[] | null>(null);
  const [topExposuresLoading, setTopExposuresLoading] = useState(false);
  const [topExposuresError, setTopExposuresError] = useState<string | null>(null);
  const [openDepths, setOpenDepths] = useState<Record<number, boolean>>({});

  // Load all funds plus the batch parent-fund list in two round trips, so the
  // picker only surfaces actual fund-of-funds. Direct-investment funds
  // shouldn't appear here — the look-through panel doesn't say anything
  // useful about them.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [fundsRes, parentsRes] = await Promise.all([
          axios.get<FundOption[]>('/api/alternatives/funds'),
          axios.get<number[]>('/api/alternatives/fof/parents'),
        ]);
        if (cancelled) return;
        const all = Array.isArray(fundsRes.data) ? fundsRes.data : [];
        setFunds(all);
        const parentIds = new Set(Array.isArray(parentsRes.data) ? parentsRes.data : []);
        const fofs = all.filter((f) => parentIds.has(f.id));
        setFofFunds(fofs);
        if (fofs.length > 0 && selectedFundId == null) {
          setSelectedFundId(fofs[0].id);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load funds.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedFundId == null) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get<LookthroughResult>(
          `/api/alternatives/fof/${selectedFundId}/lookthrough`,
        );
        if (!cancelled) setResult(r.data);
      } catch (e: any) {
        if (!cancelled)
          setError(
            e?.response?.data?.message || e?.message || 'Failed to load look-through.',
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFundId]);

  // by-depth bucket fetch
  useEffect(() => {
    if (selectedFundId == null || viewMode !== 'byDepth') return;
    let cancelled = false;
    (async () => {
      setByDepthLoading(true);
      setByDepthError(null);
      try {
        const r = await axios.get<Record<number, DepthBucket>>(
          `/api/alternatives/fof/${selectedFundId}/by-depth`,
          { params: { perDepthLimit: 25 } },
        );
        if (!cancelled) {
          setByDepthData(r.data || {});
          // open the lowest depth by default for orientation
          const keys = Object.keys(r.data || {}).map(Number);
          if (keys.length > 0) {
            setOpenDepths({ [Math.min(...keys)]: true });
          }
        }
      } catch (e: any) {
        if (!cancelled)
          setByDepthError(
            e?.response?.data?.message || e?.message || 'Failed to load depth buckets.',
          );
      } finally {
        if (!cancelled) setByDepthLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFundId, viewMode]);

  // top exposures with maxDepth fetch
  useEffect(() => {
    if (selectedFundId == null || viewMode !== 'topExposures') return;
    let cancelled = false;
    (async () => {
      setTopExposuresLoading(true);
      setTopExposuresError(null);
      try {
        const r = await axios.get<LookthroughLeaf[]>(
          `/api/alternatives/fof/${selectedFundId}/top-exposures-by-depth`,
          { params: { maxDepth, limit: topLimit } },
        );
        if (!cancelled) setTopExposures(Array.isArray(r.data) ? r.data : []);
      } catch (e: any) {
        if (!cancelled)
          setTopExposuresError(
            e?.response?.data?.message || e?.message || 'Failed to load top exposures.',
          );
      } finally {
        if (!cancelled) setTopExposuresLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFundId, viewMode, maxDepth, topLimit]);

  if (funds.length > 0 && fofFunds.length === 0) {
    return (
      <Wrap>
        <EmptyState>
          <Layers3 />
          <div style={{ fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>
            No fund-of-funds structures
          </div>
          <div style={{ fontSize: '0.85rem' }}>
            Look-through requires at least one fund holding child fund stakes
            (V293 <code>dim_fund_holding</code>). All of this tenant's funds
            invest directly in portfolio companies.
          </div>
        </EmptyState>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <HeaderRow>
        <TitleBlock>
          <Layers3 />
          <div>
            <Title>Fund-of-Funds Look-through</Title>
            <Subtitle>
              Pro-rata exposure to leaf portcos through nested fund layers
            </Subtitle>
          </div>
        </TitleBlock>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <ViewToggle>
            <ViewBtn $active={viewMode === 'leaves'} onClick={() => setViewMode('leaves')}>Leaves</ViewBtn>
            <ViewBtn $active={viewMode === 'byDepth'} onClick={() => setViewMode('byDepth')}>By Depth</ViewBtn>
            <ViewBtn $active={viewMode === 'topExposures'} onClick={() => setViewMode('topExposures')}>Top Exposures</ViewBtn>
          </ViewToggle>
          {viewMode === 'topExposures' && (
            <>
              <DepthSliderWrap>
                <label>Max Depth</label>
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={maxDepth}
                  onChange={(e) => setMaxDepth(Number(e.target.value))}
                />
                <span className="val">{maxDepth}</span>
              </DepthSliderWrap>
              <DepthSliderWrap>
                <label>Top N</label>
                <input
                  type="range"
                  min={5}
                  max={50}
                  step={5}
                  value={topLimit}
                  onChange={(e) => setTopLimit(Number(e.target.value))}
                />
                <span className="val">{topLimit}</span>
              </DepthSliderWrap>
            </>
          )}
          {fofFunds.length > 0 && (
            <Picker
              value={selectedFundId ?? ''}
              onChange={(e) =>
                setSelectedFundId(e.target.value ? Number(e.target.value) : null)
              }
            >
              {fofFunds.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.fundName}
                  {f.vintageYear ? ` · ${f.vintageYear}` : ''}
                </option>
              ))}
            </Picker>
          )}
        </div>
      </HeaderRow>

      {loading && <EmptyState>Loading look-through…</EmptyState>}

      {!loading && error && (
        <EmptyState>
          <AlertCircle />
          <div>{error}</div>
        </EmptyState>
      )}

      {!loading && !error && result && (
        <>
          <SummaryGrid>
            <SummaryCell>
              <SummaryLabel>Rolled-up NAV</SummaryLabel>
              <SummaryValue>{fmtMoney(result.totalNav)}</SummaryValue>
              <SummarySub>attributed across {result.leaves.length} leaf path{result.leaves.length === 1 ? '' : 's'}</SummarySub>
            </SummaryCell>
            <SummaryCell>
              <SummaryLabel>As of</SummaryLabel>
              <SummaryValue style={{ fontSize: '1rem' }}>{result.asOfDate}</SummaryValue>
              <SummarySub>look-through date</SummarySub>
            </SummaryCell>
            <SummaryCell>
              <SummaryLabel>Mark window</SummaryLabel>
              <SummaryValue style={{ fontSize: '0.95rem' }}>
                {result.oldestMarkDate && result.newestMarkDate
                  ? `${result.oldestMarkDate} → ${result.newestMarkDate}`
                  : '—'}
              </SummaryValue>
              <SummarySub>oldest → newest leaf valuation</SummarySub>
            </SummaryCell>
            <SummaryCell>
              <SummaryLabel>Missing marks</SummaryLabel>
              <SummaryValue>{result.missingValuations}</SummaryValue>
              <SummarySub>
                {result.missingValuations === 0
                  ? 'all leaves valued'
                  : 'leaves treated as zero'}
              </SummarySub>
            </SummaryCell>
          </SummaryGrid>

          {viewMode === 'leaves' && (
            result.leaves.length === 0 ? (
              <EmptyState>
                <Layers3 />
                <div>No leaf portcos visible at this as-of date.</div>
              </EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Portco</Th>
                    <Th>Sector</Th>
                    <Th>Depth</Th>
                    <Th style={{ textAlign: 'right' }}>Path %</Th>
                    <Th style={{ textAlign: 'right' }}>Leaf own %</Th>
                    <Th style={{ textAlign: 'right' }}>Leaf equity</Th>
                    <Th style={{ textAlign: 'right' }}>Attributed</Th>
                    <Th>Mark date</Th>
                  </tr>
                </thead>
                <tbody>
                  {result.leaves.map((l, idx) => (
                    <tr key={`${l.pathFundId}-${l.portcoId}-${idx}`}>
                      <Td>{l.portcoName}</Td>
                      <Td>{l.sector || '—'}</Td>
                      <Td>
                        <DepthChip $direct={l.pathDepth === 0}>
                          {l.pathDepth === 0 ? 'Direct' : `Lvl ${l.pathDepth}`}
                        </DepthChip>
                      </Td>
                      <TdRight>{fmtPct(l.pathOwnershipPct, 2)}</TdRight>
                      <TdRight>{fmtPct(l.portcoOwnershipPct, 1)}</TdRight>
                      <TdRight>{fmtMoney(l.portcoEquityValue)}</TdRight>
                      <TdRight style={{ color: '#e2e8f0', fontWeight: 600 }}>
                        {fmtMoney(l.attributedValue)}
                      </TdRight>
                      <TdMono>{l.markDate || '—'}</TdMono>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
          )}

          {viewMode === 'byDepth' && (
            <>
              {byDepthLoading && <EmptyState>Loading depth buckets…</EmptyState>}
              {!byDepthLoading && byDepthError && (
                <EmptyState>
                  <AlertCircle />
                  <div>{byDepthError}</div>
                </EmptyState>
              )}
              {!byDepthLoading && !byDepthError && byDepthData && Object.keys(byDepthData).length === 0 && (
                <EmptyState>
                  <Layers3 />
                  <div>No depth buckets to display.</div>
                </EmptyState>
              )}
              {!byDepthLoading && !byDepthError && byDepthData && Object.keys(byDepthData).length > 0 && (
                <>
                  {Object.entries(byDepthData)
                    .map(([k, v]) => [Number(k), v] as [number, DepthBucket])
                    .sort(([a], [b]) => a - b)
                    .map(([depth, bucket]) => {
                      const isOpen = !!openDepths[depth];
                      return (
                        <DepthBucketCard key={depth}>
                          <DepthBucketHead
                            onClick={() => setOpenDepths((s) => ({ ...s, [depth]: !s[depth] }))}
                          >
                            <DepthBucketTitle>
                              <Caret $open={isOpen}>▶</Caret>
                              <DepthChip $direct={depth === 0}>
                                {depth === 0 ? 'Direct' : `Lvl ${depth}`}
                              </DepthChip>
                              <span className="label">
                                {depth === 0 ? 'Direct portco holdings' : `Reached via ${depth} FoF layer${depth === 1 ? '' : 's'}`}
                              </span>
                              <span className="count">
                                · {bucket.portcoCount} portco{bucket.portcoCount === 1 ? '' : 's'}
                              </span>
                            </DepthBucketTitle>
                            <DepthBucketRight>{fmtMoney(bucket.totalNav)}</DepthBucketRight>
                          </DepthBucketHead>
                          {isOpen && bucket.topLeaves && bucket.topLeaves.length > 0 && (
                            <Table style={{ borderRadius: 0, border: 0 }}>
                              <thead>
                                <tr>
                                  <Th>Portco</Th>
                                  <Th>Sector</Th>
                                  <Th style={{ textAlign: 'right' }}>Path %</Th>
                                  <Th style={{ textAlign: 'right' }}>Leaf own %</Th>
                                  <Th style={{ textAlign: 'right' }}>Attributed</Th>
                                  <Th>Mark date</Th>
                                </tr>
                              </thead>
                              <tbody>
                                {bucket.topLeaves.map((l, idx) => (
                                  <tr key={`${depth}-${l.portcoId}-${idx}`}>
                                    <Td>{l.portcoName}</Td>
                                    <Td>{l.sector || '—'}</Td>
                                    <TdRight>{fmtPct(l.pathOwnershipPct, 2)}</TdRight>
                                    <TdRight>{fmtPct(l.portcoOwnershipPct, 1)}</TdRight>
                                    <TdRight style={{ color: '#e2e8f0', fontWeight: 600 }}>
                                      {fmtMoney(l.attributedValue)}
                                    </TdRight>
                                    <TdMono>{l.markDate || '—'}</TdMono>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </DepthBucketCard>
                      );
                    })}
                </>
              )}
            </>
          )}

          {viewMode === 'topExposures' && (
            <>
              {topExposuresLoading && <EmptyState>Loading top exposures…</EmptyState>}
              {!topExposuresLoading && topExposuresError && (
                <EmptyState>
                  <AlertCircle />
                  <div>{topExposuresError}</div>
                </EmptyState>
              )}
              {!topExposuresLoading && !topExposuresError && topExposures && topExposures.length === 0 && (
                <EmptyState>
                  <Layers3 />
                  <div>No portcos visible at maxDepth = {maxDepth}.</div>
                </EmptyState>
              )}
              {!topExposuresLoading && !topExposuresError && topExposures && topExposures.length > 0 && (
                <Table>
                  <thead>
                    <tr>
                      <Th style={{ width: '40px', textAlign: 'right' }}>#</Th>
                      <Th>Portco</Th>
                      <Th>Sector</Th>
                      <Th>Depth</Th>
                      <Th style={{ textAlign: 'right' }}>Path %</Th>
                      <Th style={{ textAlign: 'right' }}>Leaf own %</Th>
                      <Th style={{ textAlign: 'right' }}>Attributed</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {topExposures.map((l, idx) => (
                      <tr key={`top-${l.portcoId}-${idx}`}>
                        <TdRight style={{ color: '#64748b' }}>{idx + 1}</TdRight>
                        <Td>{l.portcoName}</Td>
                        <Td>{l.sector || '—'}</Td>
                        <Td>
                          <DepthChip $direct={l.pathDepth === 0}>
                            {l.pathDepth === 0 ? 'Direct' : `Lvl ${l.pathDepth}`}
                          </DepthChip>
                        </Td>
                        <TdRight>{fmtPct(l.pathOwnershipPct, 2)}</TdRight>
                        <TdRight>{fmtPct(l.portcoOwnershipPct, 1)}</TdRight>
                        <TdRight style={{ color: '#e2e8f0', fontWeight: 600 }}>
                          {fmtMoney(l.attributedValue)}
                        </TdRight>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </>
      )}
    </Wrap>
  );
};

export default FoFLookthroughPanel;
